import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { PalType } from "../types/Pal";
import { FaTimes } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import { getAllCategories } from "../backend/api/categories";
import { useFiltersStore } from "../stores/FiltersStore";
import GearButton from "../ui/gearbutton";
// useWelcome removed

interface FiltersProps {
  palTypes: PalType[];
}

// Tipo per i dati delle categorie dal server
interface CategoryData {
  id: string;
  category: string;
  subcategory: string;
  type: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Struttura gerarchica per i menu
interface HierarchicalCategories {
  [category: string]: {
    [subcategory: string]: Array<{ id: string; type: string }>;
  };
}

const Filters: React.FC<FiltersProps> = ({ palTypes = [] }) => {
  const { t, i18n } = useTranslation();

  // Usa lo store Zustand invece degli state locali
  const {
    isOpen,
    selectedTypes,
    zipCode,
    searchRadius,
    selectedCategory,
    selectedSubcategory,
    selectedType,
    setIsOpen,
    setSelectedTypes,
    setZipCode,
    setSearchRadius,
    setSelectedCategory,
    setSelectedSubcategory,
    setSelectedType,
    clearAllFilters,
  } = useFiltersStore();

  // Carica le categorie dal server nella lingua corrente
  const { data: categoriesData = [], isLoading: categoriesLoading } = useQuery<
    CategoryData[]
  >({
    queryKey: ["categories", i18n.language],
    queryFn: () => getAllCategories(i18n.language),
  });

  // Trasforma i dati flat del server in struttura gerarchica
  const hierarchicalCategories = useMemo(() => {
    const result: HierarchicalCategories = {};

    categoriesData.forEach((item) => {
      if (!item.isActive) return; // Salta le categorie non attive

      if (!result[item.category]) {
        result[item.category] = {};
      }

      if (!result[item.category][item.subcategory]) {
        result[item.category][item.subcategory] = [];
      }

      result[item.category][item.subcategory].push({
        id: item.id,
        type: item.type,
      });
    });

    return result;
  }, [categoriesData]);

  // Opzioni per il menu delle categorie principali
  const categoryOptions = useMemo(() => {
    return Object.keys(hierarchicalCategories).map((cat) => ({
      value: cat,
      label: cat,
    }));
  }, [hierarchicalCategories]);

  // Opzioni per il menu delle sottocategorie
  const subcategoryOptions = useMemo(() => {
    if (!selectedCategory || !hierarchicalCategories[selectedCategory])
      return [];

    return Object.keys(hierarchicalCategories[selectedCategory]).map((sub) => ({
      value: sub,
      label: sub,
    }));
  }, [selectedCategory, hierarchicalCategories]);

  // Opzioni per il menu dei tipi
  const typeOptions = useMemo(() => {
    if (!selectedCategory || !selectedSubcategory) return [];

    const types =
      hierarchicalCategories[selectedCategory]?.[selectedSubcategory] || [];

    return types.map((item) => ({
      value: item.type,
      label: item.type,
    }));
  }, [selectedCategory, selectedSubcategory, hierarchicalCategories]);

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 5 && /^\d*$/.test(value)) {
      setZipCode(value);
    }
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const radius = parseInt(e.target.value);
    setSearchRadius(radius);
  };

  const updateFilters = (updates: { types?: PalType[] }) => {
    if (updates.types !== undefined) {
      setSelectedTypes(updates.types);
    }
  };

  // Calcola il numero di filtri attivi (escludendo searchRadius)
  const activeFiltersCount = useFiltersStore((state) => {
    let count = 0;
    if (state.selectedTypes.length > 0) count++;
    if (state.zipCode.trim() !== "") count++;
    if (state.selectedCategory !== "") count++;
    if (state.selectedSubcategory !== "") count++;
    if (state.selectedType !== "") count++;
    return count;
  });

  // Drawer Animation classes
  const drawerClasses = `fixed top-0 right-0 z-[2000] h-full w-full sm:w-[400px] bg-[#2D1B4E] shadow-2xl transform transition-transform duration-300 ease-in-out ${
    isOpen ? "translate-x-0" : "translate-x-full"
  }`;

  // Backdrop
  const backdropClasses = `fixed inset-0 z-[1999] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
    isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
  }`;

  return (
    <>
      {/* Backdrop */}
      <div className={backdropClasses} onClick={() => setIsOpen(false)} />

      {/* Drawer */}
      <div className={drawerClasses}>
        <div className="flex flex-col h-full text-white">
          {/* Header */}
          <div className="p-6 flex justify-between items-start">
            <div>
              <h2 className="text-[16px] font-[600] mb-1">
                {t("filters.title", "Filter Results")}
              </h2>
              <p className="text-[14px] text-[#D1D5DC]">
                {t(
                  "filters.subtitle",
                  "Refine your search to find exactly what you need"
                )}
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {/* Type Selection */}
            <div>
              <h3 className="font-medium text-[16px] mb-4">
                {t("filters.lookingFor", "I am looking for")}
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    // Toggle LivePal
                    const newTypes = selectedTypes.includes("LivePal")
                      ? selectedTypes.filter((t) => t !== "LivePal")
                      : [...selectedTypes, "LivePal" as PalType];
                    updateFilters({ types: newTypes });
                  }}
                  className={`w-full p-4 rounded-xl flex items-center gap-4 text-left transition-all border ${
                    selectedTypes.includes("LivePal")
                      ? "bg-[#3D1E6C] border-[#5A18C4]"
                      : "bg-[#3A1A60] border-[#6E11B04D] hover:bg-[#3D1E6C]"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedTypes.includes("LivePal")
                        ? "bg-[#5A18C4]"
                        : "bg-[#452775]"
                    }`}
                  >
                    <span className="text-xl">🏠</span>
                  </div>
                  <div>
                    <div className="font-bold">
                      {t("filters.liveVenue", "Live Venue")}
                    </div>
                    <div className="text-xs text-gray-300">
                      {t(
                        "filters.liveVenueDesc",
                        "Find stores and venues nearby"
                      )}
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    // Toggle GearPal
                    const isSelected = selectedTypes.includes("GearPal");
                    let newTypes = isSelected
                      ? selectedTypes.filter((t) => t !== "GearPal")
                      : [...selectedTypes, "GearPal" as PalType];

                    updateFilters({ types: newTypes });
                  }}
                  className={`w-full p-4 rounded-xl flex items-center gap-4 text-left transition-all border ${
                    selectedTypes.includes("GearPal")
                      ? "bg-[#3D1E6C] border-[#5A18C4]"
                      : "bg-[#3A1A60] border-[#6E11B04D] hover:bg-[#3D1E6C]"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedTypes.includes("GearPal")
                        ? "bg-[#5A18C4]"
                        : "bg-[#452775]"
                    }`}
                  >
                    <span className="text-xl">📦</span>
                  </div>
                  <div>
                    <div className="font-bold">
                      {t("filters.gearProvider", "Supplier")}
                    </div>
                    <div className="text-xs text-gray-300">
                      {t(
                        "filters.gearProviderDesc",
                        "Browse individual gear and equipment"
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Dynamic Filters for GearPal */}
            {selectedTypes.includes("GearPal") && (
              <div className="space-y-4 pt-4 border-t border-[#9333EA33]">
                <h3 className="font-medium">
                  {t("filters.gearDetails", "Gear Details")}
                </h3>

                {/* Category */}
                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    {t("forms.category")}
                  </label>
                  <Select
                    className="noBorderInput"
                    theme={(theme) => ({
                      ...theme,
                      borderRadius: 8,
                      colors: {
                        ...theme.colors,
                        primary: "#5A18C4",
                        neutral0: "#3D1E6C", // bg
                        neutral80: "#FFFFFF", // text
                        neutral50: "#9CA3AF", // placeholder
                        neutral10: "#2F1155", // hover
                      },
                    })}
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "#2F1155",
                        borderColor: "transparent",
                        color: "white",
                        "&:hover": { borderColor: "#5A18C4" },
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "#2F1155",
                        color: "white",
                      }),
                      singleValue: (base) => ({ ...base, color: "white" }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "#3D1E6C"
                          : "#2F1155",
                        color: "white",
                        ":active": {
                          backgroundColor: "#5A18C4",
                        },
                      }),
                      input: (base) => ({
                        ...base,
                        color: "white",
                        borderColor: "transparent !important",
                      }),
                    }}
                    isClearable
                    isLoading={categoriesLoading}
                    options={categoryOptions}
                    value={
                      selectedCategory
                        ? { value: selectedCategory, label: selectedCategory }
                        : null
                    }
                    onChange={(option) => {
                      setSelectedCategory(option?.value || "");
                      setSelectedSubcategory("");
                      setSelectedType("");
                    }}
                    placeholder={t(
                      "filters.selectCategory",
                      "Select category..."
                    )}
                  />
                </div>

                {/* Subcategory */}
                {selectedCategory && (
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">
                      {t("filters.subcategory", "Subcategory")}
                    </label>
                    <Select
                      className="noBorderInput"
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 8,
                        colors: { ...theme.colors, primary: "#5A18C4" },
                      })}
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: "#2F1155",
                          borderColor: "transparent",
                          color: "white",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#2F1155",
                          color: "white",
                        }),
                        singleValue: (base) => ({ ...base, color: "white" }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isFocused
                            ? "#3D1E6C"
                            : "#2F1155",
                          color: "white",
                        }),
                        input: (base) => ({
                          ...base,
                          color: "white",
                          borderColor: "transparent !important",
                        }),
                      }}
                      isClearable
                      options={subcategoryOptions}
                      value={
                        selectedSubcategory
                          ? {
                              value: selectedSubcategory,
                              label: selectedSubcategory,
                            }
                          : null
                      }
                      onChange={(option) => {
                        setSelectedSubcategory(option?.value || "");
                        setSelectedType("");
                      }}
                      placeholder={t(
                        "filters.selectSubcategory",
                        "Select subcategory..."
                      )}
                    />
                  </div>
                )}

                {/* Type */}
                {selectedCategory && selectedSubcategory && (
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">
                      {t("filters.type", "Type")}
                    </label>
                    <Select
                      className="noBorderInput"
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 8,
                        colors: { ...theme.colors, primary: "#5A18C4" },
                      })}
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: "#2F1155",
                          borderColor: "transparent",
                          color: "white",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#2F1155",
                          color: "white",
                        }),
                        singleValue: (base) => ({ ...base, color: "white" }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isFocused
                            ? "#3D1E6C"
                            : "#2F1155",
                          color: "white",
                        }),
                        input: (base) => ({
                          ...base,
                          color: "white",
                          borderColor: "transparent !important",
                        }),
                      }}
                      isClearable
                      options={typeOptions}
                      value={
                        selectedType
                          ? typeOptions.find(
                              (opt) => opt.value === selectedType
                            ) || null
                          : null
                      }
                      onChange={(option) =>
                        setSelectedType(option?.value || "")
                      }
                      placeholder={t("filters.selectType", "Select type...")}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Common Filters */}
            <div className="space-y-4 pt-4 border-t border-[#9333EA33]">
              <h3 className="font-medium">
                {t("filters.location", "Location Settings")}
              </h3>

              {/* Zip Code */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  {t("forms.zipCode")}
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={handleZipCodeChange}
                  placeholder={t("filters.enterZipCode", "Enter ZIP code...")}
                  className="w-full p-2 rounded-lg bg-[#2F1155] border-transparent text-white placeholder-gray-400 focus:ring-2 focus:ring-[#5A18C4] focus:outline-none"
                  maxLength={5}
                />
              </div>

              {/* Radius */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  {t("filters.searchRadius", "Search Radius")}
                </label>
                <select
                  value={searchRadius}
                  onChange={handleRadiusChange}
                  disabled={!zipCode}
                  className={`w-full p-2 rounded-lg bg-[#2F1155] border-transparent text-white focus:ring-2 focus:ring-[#5A18C4] focus:outline-none ${
                    !zipCode ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <option value={5}>5 km</option>
                  <option value={10}>10 km</option>
                  <option value={20}>20 km</option>
                  <option value={50}>50 km</option>
                  <option value={100}>100 km</option>
                </select>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-[#9333EA33] space-y-3 bg-[#2D1B4E]">
            <GearButton
              variant="secondary"
              className="w-full h-[36px] rounded-full"
              onClick={() => setIsOpen(false)}
            >
              {t("filters.apply", "Apply Filters")}
            </GearButton>
            <GearButton
              variant="white"
              className="w-full h-[36px] rounded-full"
              onClick={clearAllFilters}
            >
              <div className="flex items-center gap-2 w-full justify-center">
                <FaTimes />
                {t("filters.resetAll", "Reset All")}
              </div>
            </GearButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default Filters;
